import {
  assignValue,
  mapResults,
  selectableResultWithDates,
  findMatch,
  allSelected,
  mapItemOrderByValues,
} from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import { ATTACHMENT_TRANSFER } from '@/datasets/attachments/constants';
import actions from '../constants';
import { nextRequestStepIndex } from '../../helper';
import findRequestAttachments, { selectAttachment, allowAttachmentSelection } from './state';

const fetchRequestAttachments = withDraft((draft, action) => {
  const { attachments } = findRequestAttachments(draft, action);
  attachments.readStatus = states.PENDING;
  attachments.readErrors = [];
  attachments.results = [];
});

const showRequestAttachmentsError = withDraft((draft, action) => {
  const { item, attachments } = findRequestAttachments(draft, action);
  attachments.readStatus = states.ERROR;
  attachments.readErrors = [responseError(action)];
  item.steps.activeStepIndex = nextRequestStepIndex({ request: item, ...action });
});

const markTransferable = ({ results }) => results.map((attachment) => ({
  ...attachment,
  selected: allowAttachmentSelection(attachment)
    ? attachment.selected || attachment?.metadata?.transferable === ATTACHMENT_TRANSFER.recommended.name
    : false,
}));

const showRequestAttachments = withDraft((draft, action) => {
  const { item, attachments } = findRequestAttachments(draft, action);
  attachments.readStatus = states.SUCCESS;
  attachments.readErrors = [];
  attachments.results = mapResults(attachments.results, action, selectableResultWithDates);
  attachments.results = selectAttachment(attachments);
  attachments.results = markTransferable(attachments);
  item.steps.activeStepIndex = nextRequestStepIndex({ request: item, ...action });
});

const toggleRequestAttachmentSelected = withDraft((draft, { category, code, attachmentId }) => {
  const { attachments } = findRequestAttachments(draft, { category, code });
  const attachment = findMatch(attachments.results, attachmentId, 'id');

  if (attachment && allowAttachmentSelection(attachment)) {
    attachment.selected = !attachment.selected;
  }
});

const toggleRequestAttachmentsSelected = withDraft((draft, { category, code, selected }) => {
  const { attachments } = findRequestAttachments(draft, { category, code });
  const newSelectedValue = selected ?? !allSelected(attachments.results.filter(allowAttachmentSelection));

  attachments.results.forEach((attachment) => {
    if (allowAttachmentSelection(attachment)) {
      assignValue(attachment, 'selected', newSelectedValue);
    }
  });
});

const toggleRequestAttachmentColumnOrderBy = withDraft((draft, { category, code, orderByColumn }) => {
  const { attachments } = findRequestAttachments(draft, { category, code });

  if (attachments) {
    attachments.orderBy = mapItemOrderByValues(attachments.orderBy, orderByColumn, true);
  }
});

const toggleRequestAttachmentTypeFilter = withDraft((draft, { category, code, attachmentType }) => {
  const { filters } = findRequestAttachments(draft, { category, code });

  if (filters) {
    if (filters.byType[attachmentType]) {
      delete filters.byType[attachmentType];
    } else { filters.byType[attachmentType] = true; }
  }
});

const updateRequestAttachmentTextFilter = withDraft((draft, { category, code, text }) => {
  const { filters } = findRequestAttachments(draft, { category, code });

  if (filters) {
    filters.byTextInput = text;
  }
});

const requestAttachmentFolderPush = withDraft((draft, { category, code, folder }) => {
  const { attachments } = findRequestAttachments(draft, { category, code });

  if (attachments) {
    attachments.folders.currentPathSegments.push({ name: folder.name, id: folder.id });
    attachments.navigation.results = [];
    attachments.navigation.readStatus = states.PENDING;
    attachments.navigation.readErrors = [];
  }
});

const navigationReadStatus = (segments) => (segments.length === 0 ? states.SUCCESS : states.PENDING);

const requestAttachmentFolderPop = withDraft((draft, { category, code }) => {
  const { attachments } = findRequestAttachments(draft, { category, code });

  if (attachments) {
    attachments.folders.currentPathSegments.pop();
    attachments.navigation.results = [];
    attachments.navigation.readStatus = navigationReadStatus(attachments.folders.currentPathSegments);
    attachments.navigation.readErrors = [];
  }
});

const requestAttachmentFolderNavigate = withDraft((draft, { category, code, segments }) => {
  const { attachments } = findRequestAttachments(draft, { category, code });

  if (attachments) {
    attachments.folders.currentPathSegments = segments;
    attachments.navigation.results = [];
    attachments.navigation.readErrors = [];
    attachments.navigation.readStatus = navigationReadStatus(segments);
  }
});

const showRequestFolderAttachments = withDraft((draft, action) => {
  const { attachments } = findRequestAttachments(draft, action);

  if (attachments) {
    attachments.navigation.readStatus = states.SUCCESS;
    attachments.navigation.readErrors = [];
    attachments.navigation.results = mapResults(attachments.navigation.results, action, selectableResultWithDates);
  }
});

const showRequestFolderAttachmentsError = withDraft((draft, action) => {
  const { attachments } = findRequestAttachments(draft, action);

  if (attachments) {
    attachments.navigation.readStatus = states.ERROR;
    attachments.navigation.readErrors = [responseError(action)];
  }
});

export default {
  [actions.FETCH_REQUEST_ATTACHMENTS]: fetchRequestAttachments,
  [actions.REQUEST_ATTACHMENT_FOLDER_NAVIGATE]: requestAttachmentFolderNavigate,
  [actions.REQUEST_ATTACHMENT_FOLDER_POP]: requestAttachmentFolderPop,
  [actions.REQUEST_ATTACHMENT_FOLDER_PUSH]: requestAttachmentFolderPush,
  [actions.SHOW_REQUEST_ATTACHMENTS]: showRequestAttachments,
  [actions.SHOW_REQUEST_ATTACHMENTS_ERROR]: showRequestAttachmentsError,
  [actions.SHOW_REQUEST_FOLDER_ATTACHMENTS]: showRequestFolderAttachments,
  [actions.SHOW_REQUEST_FOLDER_ATTACHMENTS_ERROR]: showRequestFolderAttachmentsError,
  [actions.TOGGLE_REQUEST_ATTACHMENT_COLUMN_ORDER_BY]: toggleRequestAttachmentColumnOrderBy,
  [actions.TOGGLE_REQUEST_ATTACHMENT_SELECTED]: toggleRequestAttachmentSelected,
  [actions.TOGGLE_REQUEST_ATTACHMENT_TYPE_FILTER]: toggleRequestAttachmentTypeFilter,
  [actions.TOGGLE_REQUEST_ATTACHMENTS_SELECTED]: toggleRequestAttachmentsSelected,
  [actions.UPDATE_REQUEST_ATTACHMENT_TEXT_FILTER]: updateRequestAttachmentTextFilter,
};
